"use client";

import { useEffect, useState, useRef } from "react";
import type { QuoteStats } from "@/app/api/quote-stats/route";

/* ─── Types ─── */
export type PeerBenchmarkVariant = "route" | "bandwidth" | "provisioning" | "product";

export interface PeerBenchmarkContext {
  origin?: string;
  destination?: string;
  bandwidth?: string;
  product?: string;
}

export interface PeerBenchmarkProps {
  variant: PeerBenchmarkVariant;
  context: PeerBenchmarkContext;
}

/* ─── Singleton stats cache so multiple instances share one fetch ─── */
let _statsCache: QuoteStats | null = null;
let _statsFetchPromise: Promise<QuoteStats> | null = null;

async function fetchStats(): Promise<QuoteStats> {
  if (_statsCache) return _statsCache;
  if (_statsFetchPromise) return _statsFetchPromise;

  _statsFetchPromise = fetch("/api/quote-stats")
    .then((r) => r.json() as Promise<QuoteStats>)
    .then((data) => {
      _statsCache = data;
      _statsFetchPromise = null;
      return data;
    })
    .catch(() => {
      _statsFetchPromise = null;
      throw new Error("Failed to fetch quote stats");
    });

  return _statsFetchPromise;
}

/* ─── Small sparkle icon ─── */
function TrendIcon() {
  return (
    <svg
      className="inline-block w-2.5 h-2.5 mr-1 shrink-0"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="6" cy="6" r="2" fill="currentColor" opacity="0.6" />
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" opacity="0.25" />
    </svg>
  );
}

/* ─── Component ─── */
export function PeerBenchmark({ variant, context }: PeerBenchmarkProps) {
  const [stats, setStats] = useState<QuoteStats | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchStats()
      .then((data) => {
        if (mountedRef.current) setStats(data);
      })
      .catch(() => {
        // Silent — benchmark is non-essential UI
      });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (!stats) return null;

  /* ── Render logic per variant ── */

  if (variant === "route") {
    const { origin, destination } = context;
    if (!origin || !destination) return null;

    const match = stats.topRoutes.find(
      (r) =>
        (r.origin === origin && r.destination === destination) ||
        (r.origin === destination && r.destination === origin)
    );
    if (!match) return null;

    return (
      <p className="flex items-center text-[10px] text-muted-foreground mt-0.5 leading-tight">
        <TrendIcon />
        Top requested route this month ·{" "}
        <span className="font-semibold ml-0.5" style={{ color: "var(--accent)" }}>
          {match.count} {match.count === 1 ? "quote" : "quotes"}
        </span>
      </p>
    );
  }

  if (variant === "bandwidth") {
    const { bandwidth } = context;
    if (!bandwidth) return null;

    const total = stats.topBandwidths.reduce((sum, b) => sum + b.count, 0);
    const match = stats.topBandwidths.find((b) => b.bandwidth === bandwidth);
    if (!match || total === 0) return null;

    const pct = Math.round((match.count / total) * 100);

    return (
      <p className="flex items-center text-[10px] text-muted-foreground mt-0.5 leading-tight">
        <TrendIcon />
        <span className="font-semibold mr-0.5" style={{ color: "var(--accent)" }}>
          {pct}%
        </span>{" "}
        of carriers on this route choose{" "}
        <span className="font-semibold ml-0.5" style={{ color: "var(--accent)" }}>
          {bandwidth}
        </span>
      </p>
    );
  }

  if (variant === "provisioning") {
    const days = stats.avgProvisioningDays;

    return (
      <p className="flex items-center text-[10px] text-muted-foreground mt-0.5 leading-tight">
        <TrendIcon />
        Avg provisioning:{" "}
        <span className="font-semibold mx-0.5" style={{ color: "var(--accent)" }}>
          {days} days
        </span>{" "}
        for last 30
      </p>
    );
  }

  if (variant === "product") {
    const { product } = context;
    if (!product) return null;

    const count = stats.byProduct[product];
    if (!count) return null;

    const label =
      product === "ip-transit"
        ? "IP Transit"
        : product === "dwdm"
        ? "DWDM"
        : product === "l2vpn"
        ? "L2VPN"
        : product === "dark-fiber"
        ? "Dark Fiber"
        : product;

    // Singular / plural — English pluralises "quote" on 1. Bulgarian handling
      // is not needed here: this component is EN-only content. If the strip is
      // ever localised the phrase belongs in i18n with proper pluralisation.
    const quoteWord = count === 1 ? "quote" : "quotes";
    return (
      <p className="flex items-center text-[10px] text-muted-foreground mt-0.5 leading-tight">
        <TrendIcon />
        <span className="font-semibold mr-0.5" style={{ color: "var(--accent)" }}>
          {count}
        </span>{" "}
        {label} {quoteWord} in last 30 days
      </p>
    );
  }

  return null;
}
